/**
 * Connected *Uložiť report* (PRD-0076) for a research run — the research page header and
 * the Report tab of the chat sheet mount it. Renders nothing until the run has finished
 * with a report. Announces the outcome: created or refreshed, and the sources whose
 * reference named nothing in the case (kept in the report as text only).
 */

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { SaveReportButton } from '@/components/research/SaveReportButton'
import { investigationPath } from '@/features/analytic-hypotheses/paths'
import { type HypothesisRun, isRunFinished, type SavedResearchReport } from '@/types/hypothesis'
import { useSaveResearchReport } from './hooks/use-research-report'

type Translate = ReturnType<typeof useTranslation>['t']

function announce(saved: SavedResearchReport, t: Translate, open: (id: string) => void) {
  const dropped = saved.droppedSources
  toast.success(t(saved.created ? 'research.saveReport.saved' : 'research.saveReport.updated'), {
    description:
      dropped.length > 0
        ? t('research.saveReport.dropped', {
            count: dropped.length,
            sources: dropped.map(source => `S${source.index}`).join(', '),
          })
        : undefined,
    action: {
      label: t('research.saveReport.open'),
      onClick: () => open(saved.investigation.id),
    },
  })
}

export function SaveResearchReportAction({
  caseId,
  run,
}: {
  caseId: string
  run: HypothesisRun | undefined
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const save = useSaveResearchReport(caseId)

  if (!run || !isRunFinished(run.status) || !run.reportMd) return null

  const open = (investigationId: string) => navigate(investigationPath(caseId, investigationId))
  const onSave = () =>
    save.mutate(
      { hypothesisId: run.hypothesisId, runId: run.id },
      {
        onSuccess: saved => announce(saved, t, open),
        onError: error => toast.error(t('research.saveReport.failed', { message: error.message })),
      }
    )

  return (
    <SaveReportButton
      savedInvestigationId={run.reportInvestigationId ?? null}
      saving={save.isPending}
      onSave={onSave}
      onOpen={open}
    />
  )
}
